import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QUERY_KEYS } from "./query-keys";
import { toast } from "sonner";

export const usePlayers = (userId?: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.players(userId),
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("players")
        .select("*")
        .eq("created_by", userId)
        .order("name");

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });
};

export const usePlayer = (playerId?: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.player(playerId),
    queryFn: async () => {
      if (!playerId) return null;
      const { data, error } = await supabase
        .from("players")
        .select("*")
        .eq("id", playerId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!playerId,
  });
};

export const useCreatePlayer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (player: { name: string; nickname?: string | null; created_by: string }) => {
      const { data, error } = await supabase
        .from("players")
        .insert([player])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.players(data.created_by) });
      toast.success("Player created successfully");
    },
    onError: (error: any) => {
      toast.error(`Error creating player: ${error.message}`);
    },
  });
};

export const useUpdatePlayer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (player: { id: string; name: string; nickname?: string | null; avatar_url?: string | null }) => {
      const update: Record<string, any> = { name: player.name, nickname: player.nickname };
      if (player.avatar_url !== undefined) update.avatar_url = player.avatar_url;
      const { data, error } = await supabase
        .from("players")
        .update(update)
        .eq("id", player.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.players(data.created_by) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.player(data.id) });
      toast.success("Player updated successfully");
    },
    onError: (error: any) => {
      toast.error(`Error updating player: ${error.message}`);
    },
  });
};

export const useDeletePlayer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      const { error } = await supabase.from("players").delete().eq("id", id);
      if (error) throw error;
      return { id, userId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.players(data.userId) });
      toast.success("Player deleted successfully");
    },
    onError: (error: any) => {
      toast.error(`Error deleting player: ${error.message}`);
    },
  });
};
